import React, { useState } from 'react';
import { ArrowRight, FileText, Briefcase, GraduationCap, Menu, X } from 'lucide-react';
import { useAuth, AuthModal } from '../auth/AuthComponents';
import { useNavigate } from 'react-router-dom';
import HeroParticles from './HeroParticles';
import DocxsAiLogo from '../shared/DocxsAiLogo';

const MobileMerderHomePage = ({ setShowAuthModal: setShowAuthModalProp }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAuthModalLocal, setShowAuthModalLocal] = useState(false);
  const setShowAuthModal = setShowAuthModalProp || setShowAuthModalLocal;
  const showAuthModal = setShowAuthModalProp ? undefined : showAuthModalLocal;

  const navLinks = [
    { name: 'Home', action: () => navigate('/home'), current: true },
    { name: 'About', action: () => navigate('/about'), current: false },
    { name: 'Pricing', action: () => navigate('/pricing'), current: false },
  ];

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      setShowAuthModal(true);
    }
  };

  const brandCta =
    'brand-gradient-btn w-full px-4 py-3 rounded-xl';

  return (
    <div className="min-h-screen public-page-glow flex flex-col relative overflow-hidden">
      <HeroParticles />
      <nav className="bg-[var(--color-surface)] backdrop-blur-md border-b border-[var(--color-border)] sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <DocxsAiLogo className="h-8 w-auto" />
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-muted)] transition-colors"
            aria-label="Toggle mobile menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="border-t border-[var(--color-border)] bg-[var(--color-surface)]">
            <div className="px-4 pt-3 pb-3 space-y-3">
              {navLinks.map((link) => (
                link.action ? (
                  <button
                    key={link.name}
                    onClick={() => {
                      link.action();
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full text-left block px-4 py-3 rounded-md text-base font-medium transition-colors ${
                      link.current
                        ? 'text-[var(--color-primary)] bg-[var(--color-primary-soft)]'
                        : 'text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]'
                    }`}
                  >
                    {link.name}
                  </button>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    className={`block px-4 py-3 rounded-md text-base font-medium transition-colors ${
                      link.current
                        ? 'text-[var(--color-primary)] bg-[var(--color-primary-soft)]'
                        : 'text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]'
                    }`}
                  >
                    {link.name}
                  </a>
                )
              ))}
            </div>
            <div className="pt-4 pb-6 border-t border-[var(--color-border)] px-4">
              {isAuthenticated ? (
                <button
                  onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                  className={brandCta}
                >
                  Dashboard
                </button>
              ) : (
                <button
                  onClick={() => { setShowAuthModal(true); setMobileMenuOpen(false); }}
                  className={brandCta}
                >
                  Get Started
                </button>
              )}
            </div>
          </div>
        )}
      </nav>

      <div className="relative z-10 text-center mt-8 mb-6 px-4">
        <h1 className="text-3xl font-bold text-[var(--color-text)] mb-3 leading-tight">
          AI-Powered <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">Document Intelligence</span>
        </h1>
        <p className="text-base text-[var(--color-text-muted)] mb-5">
          Instantly analyze, optimize, and perfect your contracts, CVs, and SOPs with AI.
        </p>
        <button
          onClick={handleGetStarted}
          className="brand-gradient-btn hero-cta-pulse w-full px-6 py-3 rounded-xl text-lg"
        >
          Get Started Free
        </button>
      </div>

      <div className="relative z-10 px-4 mt-4 mb-8">
        <div className="space-y-3">
          <div className="flex items-center elevated-card rounded-xl p-4 transition-transform duration-200 active:scale-[0.98]">
            <FileText className="w-7 h-7 text-[var(--color-primary)] mr-3" />
            <div>
              <div className="text-[var(--color-text)] font-semibold">Contract Analysis</div>
              <div className="text-[var(--color-text-muted)] text-sm">AI-powered risk detection & suggestions</div>
            </div>
          </div>
          <div className="flex items-center elevated-card rounded-xl p-4 transition-transform duration-200 active:scale-[0.98]">
            <Briefcase className="w-7 h-7 text-[var(--color-primary)] mr-3" />
            <div>
              <div className="text-[var(--color-text)] font-semibold">CV Optimization</div>
              <div className="text-[var(--color-text-muted)] text-sm">Match your resume to job roles</div>
            </div>
          </div>
          <div className="flex items-center elevated-card rounded-xl p-4 transition-transform duration-200 active:scale-[0.98]">
            <GraduationCap className="w-7 h-7 text-[var(--color-accent)] mr-3" />
            <div>
              <div className="text-[var(--color-text)] font-semibold">SOP Enhancement</div>
              <div className="text-[var(--color-text-muted)] text-sm">Perfect your statement of purpose</div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 px-4 mb-8">
        <div className="elevated-card bg-[var(--color-primary-soft)] border-[var(--color-primary)]/30 rounded-2xl p-5 text-center">
          <div className="text-[var(--color-text)] font-bold text-lg mb-1">Built for real document work</div>
          <div className="text-[var(--color-text-secondary)] text-sm mb-2">
            Analyze, revamp, and generate CVs, contracts, and SOPs — we&apos;re still shipping in public.
          </div>
          <button
            onClick={handleGetStarted}
            className="brand-gradient-btn hero-cta-pulse w-full px-4 py-2 rounded-xl mt-2"
          >
            Try Free Now
            <ArrowRight className="w-4 h-4 ml-2 inline" />
          </button>
        </div>
      </div>

      <AuthModal isOpen={setShowAuthModalProp ? false : showAuthModalLocal} onClose={() => setShowAuthModalLocal(false)} />
    </div>
  );
};

export default MobileMerderHomePage;
